<template>
  <div class="fm-page">

    <!-- Background -->
    <div class="fm-bg" :class="{ 'is-blurred': isLoading }"></div>

    <!-- Loading overlay -->
    <transition name="fm-fade">
      <div v-if="isLoading" class="fm-loading-overlay">
        <div class="fm-loading-card">
          <div class="fm-logo fm-logo--lg">FM</div>
          <div class="fm-loading-text">{{ loadingMessage }}</div>
          <div class="fm-loading-bar">
            <div class="fm-loading-fill" :style="{ width: loadingProgress + '%' }"></div>
          </div>
          <div class="fm-loading-tip">{{ currentTip }}</div>
        </div>
      </div>
    </transition>

    <!-- Content -->
    <div class="fm-content" :class="{ 'is-blurred': isLoading }">

      <div class="fm-wrap">

        <!-- ===================================================== -->
        <!-- HERO HEADER                                           -->
        <!-- ===================================================== -->
        <header class="fm-hero">
          <div class="fm-hero-grid">
            <div class="fm-hero-main">
              <div class="fm-hero-label">Dashboard</div>
              <div class="fm-hero-title">
                Welcome back, {{ userDetails.username || 'User' }}
                <span class="fm-hero-wave">👋</span>
              </div>
              <div class="fm-hero-tags">
                <span class="fm-tag is-orange">
                  <b-icon icon="person-badge"></b-icon> {{ formatUserRole(userDetails.userrole) }}
                </span>
                <span class="fm-tag">
                  <b-icon icon="calendar-event"></b-icon> {{ currentDate }}
                </span>
              </div>
            </div>
            <div class="fm-hero-qr">
              <b-icon icon="speedometer2" font-scale="2.6"></b-icon>
              <div>
                <div class="fm-qr-title">Business overview</div>
                <div class="fm-qr-sub">
                  {{ availableTabs.length }} dashboard{{ availableTabs.length === 1 ? '' : 's' }} available
                </div>
              </div>
            </div>
          </div>
        </header>

        <!-- ===================================================== -->
        <!-- TAB STRIP                                             -->
        <!-- ===================================================== -->
        <nav class="fm-tabs" role="tablist">
          <button
            v-for="tab in availableTabs"
            :key="tab.key"
            type="button"
            role="tab"
            class="fm-tab"
            :class="{ 'is-active': activeTab === tab.key }"
            @click="switchTab(tab.key)"
          >
            <span class="fm-tab-icon"><b-icon :icon="tab.iconName"></b-icon></span>
            <span class="fm-tab-label">{{ tab.label }}</span>
          </button>
        </nav>

        <!-- ===================================================== -->
        <!-- TAB CONTENT                                           -->
        <!-- ===================================================== -->
        <transition name="fm-tab-fade" mode="out-in">
          <div :key="activeTab" class="fm-tab-content">

            <!-- OVERVIEW -->
            <div v-if="activeTab === 'overview'" class="fm-tab-panel">
              <module-cards
                :user-details="userDetails"
                :search-query="searchQuery"
              />
            </div>

            <!-- SALES -->
            <div v-else-if="activeTab === 'sales'" class="fm-tab-panel">
              <div class="fm-tab-header">
                <div class="fm-tab-header-left">
                  <div class="fm-tab-header-label">Sales</div>
                  <div class="fm-tab-header-title">
                    {{ showDetailSales ? 'Detail view' : 'Summary view' }}
                  </div>
                </div>
                <div class="fm-tab-header-right">
                  <button class="fm-toggle-btn" @click="toggleSalesView" :disabled="loadingComponent === 'sales'">
                    <b-icon :icon="showDetailSales ? 'bar-chart-fill' : 'graph-up-arrow'"></b-icon>
                    {{ showDetailSales ? 'Show summary' : 'Show detail' }}
                  </button>
                </div>
              </div>

              <div v-if="!showDetailSales" class="fm-component">
                <sales-dashboard2
                  :user-details="userDetails"
                  :show-sales-dashboard="showSalesDashboard"
                  @show-stat-popup="showStatPopup"
                  @api-loading="handleApiLoading"
                  @api-loaded="handleApiLoaded"
                />
              </div>
              <div v-else-if="showDetailSales && componentsLoaded.sales" class="fm-component">
                <sales-dashboard
                  :user-details="userDetails"
                  :show-sales-dashboard="showSalesDashboard"
                  @show-stat-popup="showStatPopup"
                  @api-loading="handleApiLoading"
                  @api-loaded="handleApiLoaded"
                />
              </div>
              <div v-else-if="loadingComponent === 'sales'" class="fm-loading-inline">
                <b-spinner variant="primary"></b-spinner>
                <span>Loading detail dashboard…</span>
              </div>
            </div>

            <!-- PERFORMANCE -->
            <div v-else-if="activeTab === 'performance'" class="fm-tab-panel">
              <div class="fm-tab-header">
                <div class="fm-tab-header-left">
                  <div class="fm-tab-header-label">Performance</div>
                  <div class="fm-tab-header-title">
                    {{ showDetailPerformance ? 'Detail view' : 'Summary view' }}
                  </div>
                </div>
                <div class="fm-tab-header-right">
                  <button class="fm-toggle-btn" @click="togglePerformanceView" :disabled="loadingComponent === 'topPerformance'">
                    <b-icon :icon="showDetailPerformance ? 'bar-chart-fill' : 'trophy-fill'"></b-icon>
                    {{ showDetailPerformance ? 'Show summary' : 'Show detail' }}
                  </button>
                </div>
              </div>

              <div v-if="!showDetailPerformance" class="fm-component">
                <top-performance2
                  :user-details="userDetails"
                  :show-top-performance="showTopPerformance"
                  @api-loading="handleApiLoading"
                  @api-loaded="handleApiLoaded"
                />
              </div>
              <div v-else-if="showDetailPerformance && componentsLoaded.topPerformance" class="fm-component">
                <top-performance
                  :user-details="userDetails"
                  :show-top-performance="showTopPerformance"
                  @api-loading="handleApiLoading"
                  @api-loaded="handleApiLoaded"
                />
              </div>
              <div v-else-if="loadingComponent === 'topPerformance'" class="fm-loading-inline">
                <b-spinner variant="primary"></b-spinner>
                <span>Loading detail dashboard…</span>
              </div>
            </div>

            <!-- PRODUCTS -->
            <div v-else-if="activeTab === 'products'" class="fm-tab-panel">
              <div class="fm-tab-header">
                <div class="fm-tab-header-left">
                  <div class="fm-tab-header-label">Best sellers</div>
                  <div class="fm-tab-header-title">Product insights</div>
                </div>
                <div class="fm-tab-header-right" v-if="showBestSellersDashboard && !componentsLoaded.bestSellers">
                  <button class="fm-toggle-btn" @click="loadComponent('bestSellers')" :disabled="loadingComponent === 'bestSellers'">
                    <b-spinner small v-if="loadingComponent === 'bestSellers'"></b-spinner>
                    <b-icon v-else icon="download"></b-icon>
                    {{ loadingComponent === 'bestSellers' ? 'Loading…' : 'Load dashboard' }}
                  </button>
                </div>
              </div>

              <div v-if="componentsLoaded.bestSellers" class="fm-component">
                <best-sellers-dashboard
                  :user-details="userDetails"
                  :show-best-sellers-dashboard="showBestSellersDashboard"
                  @api-loading="handleApiLoading"
                  @api-loaded="handleApiLoaded"
                />
              </div>
              <div v-else-if="showBestSellersDashboard" class="fm-lazy-prompt">
                <div class="fm-lazy-card">
                  <span class="fm-lazy-icon"><b-icon icon="crown-fill"></b-icon></span>
                  <h4>Best sellers dashboard</h4>
                  <p>Load top products, categories, and inventory insights.</p>
                  <button class="fm-toggle-btn" @click="loadComponent('bestSellers')" :disabled="loadingComponent === 'bestSellers'">
                    <b-spinner small v-if="loadingComponent === 'bestSellers'"></b-spinner>
                    <b-icon v-else icon="download"></b-icon>
                    {{ loadingComponent === 'bestSellers' ? 'Loading…' : 'Load dashboard' }}
                  </button>
                </div>
              </div>
            </div>

            <!-- SHIPPING -->
            <div v-else-if="activeTab === 'shipping'" class="fm-tab-panel">
              <div class="fm-tab-header">
                <div class="fm-tab-header-left">
                  <div class="fm-tab-header-label">Shipping &amp; logistics</div>
                  <div class="fm-tab-header-title">Delivery tracking and receiving logs</div>
                </div>
                <div class="fm-tab-header-right" v-if="showShippingReceivingDashboard && !componentsLoaded.shipping">
                  <button class="fm-toggle-btn" @click="loadComponent('shipping')" :disabled="loadingComponent === 'shipping'">
                    <b-spinner small v-if="loadingComponent === 'shipping'"></b-spinner>
                    <b-icon v-else icon="download"></b-icon>
                    {{ loadingComponent === 'shipping' ? 'Loading…' : 'Load dashboard' }}
                  </button>
                </div>
              </div>

              <div v-if="componentsLoaded.shipping" class="fm-component">
                <shipping-receiving-dashboard
                  :user-details="userDetails"
                  :show-shipping-receiving-dashboard="showShippingReceivingDashboard"
                  @api-loading="handleApiLoading"
                  @api-loaded="handleApiLoaded"
                />
              </div>
              <div v-else-if="showShippingReceivingDashboard" class="fm-lazy-prompt">
                <div class="fm-lazy-card">
                  <span class="fm-lazy-icon"><b-icon icon="truck"></b-icon></span>
                  <h4>Shipping dashboard</h4>
                  <p>Load delivery tracking, receiving logs, and shipment analytics.</p>
                  <button class="fm-toggle-btn" @click="loadComponent('shipping')" :disabled="loadingComponent === 'shipping'">
                    <b-spinner small v-if="loadingComponent === 'shipping'"></b-spinner>
                    <b-icon v-else icon="download"></b-icon>
                    {{ loadingComponent === 'shipping' ? 'Loading…' : 'Load dashboard' }}
                  </button>
                </div>
              </div>
            </div>

          </div>
        </transition>

      </div>
    </div>

    <!-- ===================================================== -->
    <!-- STAT POPUP MODAL                                      -->
    <!-- ===================================================== -->
    <b-modal v-model="showStatPopupModal" size="md" hide-footer centered content-class="fm-modal">
      <template #modal-header>
        <div class="fm-modal-title">
          <span class="fm-modal-title-icon"><b-icon icon="graph-up-arrow"></b-icon></span>
          <div>
            <div class="fm-modal-title-main">{{ popupData.title }}</div>
            <div class="fm-modal-title-sub">Detailed breakdown</div>
          </div>
        </div>
      </template>

      <div class="fm-modal-body">
        <div class="popup-main-value" :class="popupData.trendClass">
          {{ popupData.mainValue }}
        </div>

        <div v-if="popupData.trend" class="popup-trend">
          <b-icon :icon="popupData.trendIcon"></b-icon>
          {{ popupData.trend }} vs previous period
        </div>

        <div v-if="popupData.breakdown" class="popup-breakdown">
          <h6>Breakdown</h6>
          <ul>
            <li
              v-for="(item, idx) in popupData.breakdown"
              :key="item.label"
              :style="{ animationDelay: idx * 0.05 + 's' }"
            >
              <span>{{ item.label }}</span>
              <strong>{{ item.value }}</strong>
            </li>
          </ul>
        </div>

        <div class="fm-modal-foot">
          <b-button class="fm-btn-ghost" @click="showStatPopupModal = false">Close</b-button>
          <b-button class="fm-btn-primary" @click="exportStatData(popupData.type)">
            <b-icon icon="download"></b-icon> Export data
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import ModuleCards from './Homepagecomponents/ModuleCards.vue'
import SalesDashboard2 from './Homepagecomponents/SalesDashboard2.vue';
import TopPerformance2 from './Homepagecomponents/TopPerformance2.vue'
import moment from 'moment';

export default {
  name: "Home",
  components: {
    ModuleCards,
    TopPerformance2,
    SalesDashboard2,
  },
  data() {
    return {
      userDetails: {},
      searchQuery: "",
      activeTab: 'overview',
      showStatPopupModal: false,
      popupData: {
        title: '',
        type: '',
        mainValue: '',
        trend: null,
        trendClass: '',
        trendIcon: '',
        breakdown: null
      },
      showDetailSales: false,
      showDetailPerformance: false,
      componentsLoaded: {
        topPerformance: false,
        sales: false,
        bestSellers: false,
        shipping: false
      },
      loadingComponent: null,
      isLoading: false,
      loadingMessage: 'Loading Dashboard',
      loadingProgress: 0,
      currentTipIndex: 0,
      loadingInterval: null,
      apiCallStack: 0,
      tips: [
        "Fetching sales data…",
        "Loading performance metrics…",
        "Analyzing top products…",
        "Retrieving shipping updates…",
        "Calculating commissions…",
        "Syncing inventory data…",
        "Preparing your dashboard…"
      ],
      tipInterval: null,
      msgInterval: null
    }
  },
  computed: {
    currentDate() {
      return moment().format('MMM DD, YYYY');
    },
    availableTabs() {
      const tabs = [
        { key: 'overview', label: 'Home', iconName: 'grid-3x3-gap-fill' }
      ];
      if (this.showSalesDashboard) {
        tabs.push({ key: 'sales', label: 'Sales', iconName: 'graph-up-arrow' });
      }
      if (this.showTopPerformance) {
        tabs.push({ key: 'performance', label: 'Performance', iconName: 'trophy-fill' });
      }
      // if (this.showBestSellersDashboard) {
      //   tabs.push({ key: 'products', label: 'Best sellers', iconName: 'box-seam' });
      // }
      // if (this.showShippingReceivingDashboard) {
      //   tabs.push({ key: 'shipping', label: 'Shipping', iconName: 'truck' });
      // }
      return tabs;
    },
    showSalesDashboard() {
      return this.showModule(['admin', 'accountmanager', 'officeuser', 'officemanager', 'locationadmin', 'salesperson', 'warehouseuser', 'warehousemanager']);
    },
    showBestSellersDashboard() {
      return this.showModule(['admin', 'accountmanager', 'officeuser', 'officemanager', 'locationadmin', 'salesperson']);
    },
    showShippingReceivingDashboard() {
      return this.showModule(['admin', 'accountmanager', 'officeuser', 'officemanager', 'locationadmin', 'locationstockperson', 'salesperson', 'warehouseuser', 'warehousemanager', 'housetruck', 'deliverytruck']);
    },
    showTopPerformance() {
      return this.showModule(['admin', 'accountmanager', 'officeuser', 'officemanager', 'locationadmin', 'salesperson']);
    },
    currentTip() {
      return this.tips[this.currentTipIndex];
    }
  },
  watch: {
    activeTab(newTab) {
      if (newTab === 'sales' && !this.componentsLoaded.sales) {
        this.loadComponent('sales').catch(() => {});
      } else if (newTab === 'performance' && !this.componentsLoaded.topPerformance) {
        this.loadComponent('topPerformance').catch(() => {});
      }
    }
  },
  beforeDestroy() {
    if (this.loadingInterval) clearInterval(this.loadingInterval);
    if (this.tipInterval) clearInterval(this.tipInterval);
    if (this.msgInterval) clearInterval(this.msgInterval);
  },
  methods: {
    showModule(roles) {
      return roles.includes(this.userDetails.userrole);
    },
    formatUserRole(role) {
      if (!role) return 'Unknown role';
      return role.charAt(0).toUpperCase() + role.slice(1).replace(/([A-Z])/g, ' $1');
    },
    switchTab(tabKey) {
      if (this.activeTab === tabKey) return;
      this.activeTab = tabKey;
    },
    toggleSalesView() {
      if (this.showDetailSales) {
        this.showDetailSales = false;
        this.$bvToast.toast('Switched to Summary View', { variant: 'info', autoHideDelay: 2000 });
      } else if (!this.componentsLoaded.sales) {
        this.loadComponent('sales').then(() => {
          this.showDetailSales = true;
          this.$bvToast.toast('Switched to Detail View', { variant: 'success', autoHideDelay: 2000 });
        });
      } else {
        this.showDetailSales = true;
        this.$bvToast.toast('Switched to Detail View', { variant: 'success', autoHideDelay: 2000 });
      }
    },
    togglePerformanceView() {
      if (this.showDetailPerformance) {
        this.showDetailPerformance = false;
        this.$bvToast.toast('Switched to Summary View', { variant: 'info', autoHideDelay: 2000 });
      } else if (!this.componentsLoaded.topPerformance) {
        this.loadComponent('topPerformance').then(() => {
          this.showDetailPerformance = true;
          this.$bvToast.toast('Switched to Detail View', { variant: 'success', autoHideDelay: 2000 });
        });
      } else {
        this.showDetailPerformance = true;
        this.$bvToast.toast('Switched to Detail View', { variant: 'success', autoHideDelay: 2000 });
      }
    },
    handleApiLoading(message = 'Loading data') {
      this.apiCallStack++;
      if (!this.isLoading) this.startLoadingAnimation(message);
      else this.loadingMessage = message;
    },
    handleApiLoaded() {
      this.apiCallStack--;
      if (this.apiCallStack <= 0) this.stopLoadingAnimation(true);
    },
    startLoadingAnimation(message = 'Loading Dashboard') {
      this.isLoading = true;
      this.loadingMessage = message;
      this.loadingProgress = 0;
      this.currentTipIndex = 0;

      this.loadingInterval = setInterval(() => {
        if (this.loadingProgress < 90) {
          this.loadingProgress += Math.random() * 10;
          if (this.loadingProgress > 90) this.loadingProgress = 90;
        }
      }, 300);

      this.tipInterval = setInterval(() => {
        if (this.currentTipIndex < this.tips.length - 1) this.currentTipIndex++;
      }, 2000);

      let messages = ['Loading dashboard', 'Fetching data', 'Processing information', 'Updating charts'];
      let msgIndex = 0;
      this.msgInterval = setInterval(() => {
        if (msgIndex < messages.length - 1) {
          msgIndex++;
          this.loadingMessage = messages[msgIndex];
        }
      }, 1800);
    },
    stopLoadingAnimation(success = true) {
      if (this.loadingInterval) clearInterval(this.loadingInterval);
      if (this.tipInterval) clearInterval(this.tipInterval);
      if (this.msgInterval) clearInterval(this.msgInterval);
      if (success) {
        this.loadingProgress = 100;
        setTimeout(() => { this.isLoading = false; }, 500);
      } else {
        this.isLoading = false;
      }
    },
    async loadComponent(componentName) {
      try {
        this.loadingComponent = componentName;
        this.handleApiLoading(`Loading ${this.getComponentName(componentName)}…`);

        switch (componentName) {
          case 'topPerformance': {
            const mod = await import('./Homepagecomponents/TopPerformance.vue');
            this.$options.components.TopPerformance = mod.default;
            this.componentsLoaded.topPerformance = true;
            break;
          }
          case 'bestSellers': {
            const mod = await import('./Homepagecomponents/BestSellersDashboard.vue');
            this.$options.components.BestSellersDashboard = mod.default;
            this.componentsLoaded.bestSellers = true;
            break;
          }
          case 'sales': {
            const mod = await import('./Homepagecomponents/SalesDashboard.vue');
            this.$options.components.SalesDashboard = mod.default;
            this.componentsLoaded.sales = true;
            break;
          }
          case 'shipping': {
            const mod = await import('./Homepagecomponents/ShippingReceivingDashboard.vue');
            this.$options.components.ShippingReceivingDashboard = mod.default;
            this.componentsLoaded.shipping = true;
            break;
          }
        }

        this.$bvToast.toast(`${this.getComponentName(componentName)} loaded successfully!`, {
          variant: 'success',
          autoHideDelay: 3000
        });
      } catch (error) {
        console.error(`Failed to load ${componentName}:`, error);
        this.$bvToast.toast(`Failed to load ${this.getComponentName(componentName)}`, {
          variant: 'danger',
          autoHideDelay: 5000
        });
        throw error;
      } finally {
        this.loadingComponent = null;
        this.handleApiLoaded();
      }
    },
    getComponentName(key) {
      const names = {
        topPerformance: 'Performance Dashboard',
        bestSellers: 'Best Sellers Dashboard',
        sales: 'Sales Dashboard',
        shipping: 'Shipping Dashboard'
      };
      return names[key] || key;
    },
    showStatPopup(data) {
      let popupData = {
        type: data.type,
        title: '',
        mainValue: '',
        trend: data.salesTrend,
        trendClass: this.getTrendClass(data.salesTrend),
        trendIcon: this.getTrendIcon(data.salesTrend),
        breakdown: null
      };

      switch (data.type) {
        case 'totalSales':
          popupData.title = 'Total sales overview';
          popupData.mainValue = `$${this.formatNumber(data.summaryTotals.totalSales)}`;
          popupData.breakdown = [
            { label: 'Regular sales', value: `$${this.formatNumber(data.summaryTotals.regularSales || 0)}` },
            { label: 'Layaway sales', value: `$${this.formatNumber(data.summaryTotals.layawaySales || 0)}` },
            { label: 'Average order', value: `$${this.formatNumber(data.summaryTotals.averageOrderValue)}` }
          ];
          break;
        case 'totalOrders':
          popupData.title = 'Total orders overview';
          popupData.mainValue = this.formatNumber(data.summaryTotals.totalOrders);
          popupData.breakdown = [
            { label: 'Regular orders', value: data.summaryTotals.regularOrders || 0 },
            { label: 'Layaway orders', value: data.summaryTotals.layawayOrders || 0 }
          ];
          break;
        case 'commission':
          popupData.title = 'Commission overview';
          popupData.mainValue = `$${this.formatNumber(data.summaryTotals.totalCommission)}`;
          popupData.breakdown = [
            { label: 'Base commission', value: `$${this.formatNumber(data.summaryTotals.totalCommission * 0.7)}` },
            { label: 'Bonus commission', value: `$${this.formatNumber(data.summaryTotals.totalCommission * 0.3)}` }
          ];
          break;
        case 'refund':
          popupData.title = 'Refund overview';
          popupData.mainValue = `$${this.formatNumber(data.summaryTotals.totalRefund)}`;
          popupData.breakdown = [
            { label: 'Total refunded', value: `$${this.formatNumber(data.summaryTotals.totalRefund)}` },
            { label: 'Refunded items', value: data.summaryTotals.refundItemsCount || 0 }
          ];
          break;
      }

      this.popupData = popupData;
      this.showStatPopupModal = true;
    },
    formatNumber(num) {
      if (!num && num !== 0) return '0.00';
      return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    getTrendClass(trend) {
      if (!trend && trend !== 0) return '';
      return trend >= 0 ? 'trend-positive' : 'trend-negative';
    },
    getTrendIcon(trend) {
      if (!trend && trend !== 0) return 'dash';
      return trend >= 0 ? 'arrow-up' : 'arrow-down';
    },
    exportStatData(statType) {
      this.$bvToast.toast(`Exporting ${statType} data…`, { variant: 'info' });
    }
  },
  created() {
    const userDetails = localStorage.userDetails;
    if (userDetails) this.userDetails = JSON.parse(userDetails);
  }
}
</script>

<style scoped>
/* ===================================================== */
/* PAGE SHELL                                            */
/* ===================================================== */
.fm-page {
  position: relative;
  min-height: 100vh;
  font-family: 'Manrope', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  color: #221F1C;
  font-size: 17px;
}
.fm-bg {
  position: fixed;
  inset: 0;
  background: #F6F4F0;
  z-index: -2;
}
.fm-bg::after {
  content: '';
  position: absolute;
  inset: 0;
  background-image: url('https://m.media-amazon.com/images/I/81eSegKonhL._AC_SL1500_.jpg');
  background-size: cover;
  background-position: center;
  filter: blur(12px);
  opacity: 0.35;
  transform: scale(1.08);
  transition: filter 0.3s ease;
}
.fm-bg.is-blurred::after { filter: blur(20px); }

.fm-content {
  position: relative;
  z-index: 1;
  padding: 18px 0 60px;
  transition: filter 0.3s ease;
}
.fm-content.is-blurred { filter: blur(6px); pointer-events: none; }

.fm-wrap { max-width: 1800px; width: 97%; margin: 0 auto; }
@media (max-width: 767px) {
  .fm-content { padding: 10px 0 40px; font-size: 16px; }
  .fm-wrap { width: 100%; padding: 0 12px; }
}

/* ===================================================== */
/* LOADING OVERLAY                                       */
/* ===================================================== */
.fm-loading-overlay {
  position: fixed; inset: 0; z-index: 9999;
  background: rgba(34, 31, 28, 0.82);
  backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center;
}
.fm-loading-card {
  background: #fff;
  padding: 32px 44px;
  border-radius: 20px;
  text-align: center;
  border-bottom: 5px solid #EE7A22;
  min-width: 320px;
}
.fm-logo {
  width: 54px; height: 54px; border-radius: 14px;
  background: #EE7A22; color: #fff;
  display: inline-flex; align-items: center; justify-content: center;
  font-family: 'Sora', sans-serif; font-weight: 800; font-size: 20px;
}
.fm-logo--lg { width: 64px; height: 64px; font-size: 24px; margin-bottom: 16px; animation: fmPulse 1.2s ease-in-out infinite; }
@keyframes fmPulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(0.92); } }
.fm-loading-text { font-family: 'Sora', sans-serif; font-size: 18px; font-weight: 700; margin-bottom: 16px; }
.fm-loading-bar { width: 260px; height: 6px; background: #F1EEE9; border-radius: 3px; overflow: hidden; margin: 0 auto; }
.fm-loading-fill { height: 100%; background: #EE7A22; border-radius: 3px; transition: width 0.2s ease; }
.fm-loading-tip { margin-top: 16px; font-size: 13px; color: #736C63; font-weight: 600; }

.fm-fade-enter-active, .fm-fade-leave-active { transition: opacity 0.25s ease; }
.fm-fade-enter, .fm-fade-leave-to { opacity: 0; }

/* ===================================================== */
/* HERO                                                  */
/* ===================================================== */
.fm-hero {
  background: #221F1C;
  color: #fff;
  border-radius: 20px;
  overflow: hidden;
  border-bottom: 5px solid #EE7A22;
  margin-bottom: 16px;
}
.fm-hero-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.8fr) minmax(0, 1fr);
  gap: 24px;
  padding: 22px 30px;
  align-items: center;
}
.fm-hero-label {
  font-size: 15px; color: rgba(255, 255, 255, 0.6);
  font-weight: 600; text-transform: uppercase; letter-spacing: 0.08em;
}
.fm-hero-title {
  font-family: 'Sora', sans-serif;
  font-size: 38px; font-weight: 800;
  line-height: 1.15;
  margin: 4px 0 12px;
  display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
}
.fm-hero-wave {
  display: inline-block;
  animation: wave 2s ease-in-out infinite;
  transform-origin: 70% 70%;
}
@keyframes wave {
  0% { transform: rotate(0deg); }
  10% { transform: rotate(14deg); }
  20% { transform: rotate(-8deg); }
  30% { transform: rotate(14deg); }
  40% { transform: rotate(-4deg); }
  50% { transform: rotate(10deg); }
  60% { transform: rotate(0deg); }
  100% { transform: rotate(0deg); }
}
.fm-hero-tags { display: flex; flex-wrap: wrap; gap: 8px; }
.fm-tag {
  display: inline-flex; align-items: center; gap: 7px;
  font-size: 15px; font-weight: 700;
  padding: 6px 15px; border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.14);
}
.fm-tag.is-orange { background: rgba(238, 122, 34, 0.22); color: #FFB06B; border-color: rgba(238, 122, 34, 0.45); }
.fm-hero-qr {
  display: flex; align-items: center; gap: 16px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 16px; padding: 14px 20px;
  color: #FFB06B;
}
.fm-qr-title { font-family: 'Sora', sans-serif; font-weight: 700; font-size: 17px; color: #fff; }
.fm-qr-sub { font-size: 15px; color: rgba(255, 255, 255, 0.65); }

@media (max-width: 900px) {
  .fm-hero-grid { grid-template-columns: 1fr; gap: 14px; }
  .fm-hero-title { font-size: 28px; }
}

/* ===================================================== */
/* TABS                                                  */
/* ===================================================== */
.fm-tabs {
  position: sticky;
  top: 0;
  z-index: 20;
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding: 10px;
  margin-bottom: 16px;
  background: rgba(255, 255, 255, 0.86);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid #E5E1DA;
  border-radius: 99px;
  scrollbar-width: thin;
}
.fm-tab {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  border: 1px solid #E5E1DA;
  background: #fff;
  color: #221F1C;
  padding: 8px 18px 8px 8px;
  border-radius: 99px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease, transform 0.15s ease;
}
.fm-tab:hover { border-color: #EE7A22; color: #D4620F; transform: translateY(-1px); }
.fm-tab:focus-visible { outline: 2px solid #EE7A22; outline-offset: 2px; }
.fm-tab-icon {
  width: 30px; height: 30px; border-radius: 50%;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 14px; background: #F1EEE9; color: #736C63;
  transition: all 0.15s ease;
}
.fm-tab.is-active {
  background: #EE7A22;
  border-color: #EE7A22;
  color: #fff;
  box-shadow: 0 8px 18px -8px rgba(238, 122, 34, 0.8);
}
.fm-tab.is-active .fm-tab-icon { background: #fff; color: #D4620F; }

/* ===================================================== */
/* TAB PANEL CONTENT                                     */
/* ===================================================== */
.fm-tab-fade-enter-active { animation: fmSlideIn 0.35s ease-out; }
.fm-tab-fade-leave-active { animation: fmSlideOut 0.25s ease-in; }
@keyframes fmSlideIn { from { opacity: 0; transform: translateX(12px); } to { opacity: 1; transform: translateX(0); } }
@keyframes fmSlideOut { from { opacity: 1; transform: translateX(0); } to { opacity: 0; transform: translateX(-12px); } }

.fm-tab-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  background: #fff;
  border: 1px solid #E5E1DA;
  border-radius: 16px;
  padding: 14px 18px;
  margin-bottom: 14px;
}
.fm-tab-header-label {
  font-size: 12px; font-weight: 700; color: #736C63;
  text-transform: uppercase; letter-spacing: 0.06em;
}
.fm-tab-header-title {
  font-family: 'Sora', sans-serif;
  font-size: 18px; font-weight: 700;
  color: #221F1C; margin-top: 2px;
}
.fm-tab-header-right { display: flex; gap: 8px; flex-wrap: wrap; }

.fm-toggle-btn.btn,
.fm-toggle-btn {
  background: #EE7A22;
  border: 1.5px solid #EE7A22;
  color: #fff;
  border-radius: 10px;
  padding: 9px 18px;
  font-weight: 700;
  font-size: 14.5px;
  display: inline-flex; align-items: center; gap: 8px;
  cursor: pointer;
  box-shadow: none;
  transition: all 0.15s ease;
}
.fm-toggle-btn:hover:not(:disabled) {
  background: #D4620F;
  border-color: #D4620F;
  transform: translateY(-1px);
}
.fm-toggle-btn:disabled { opacity: 0.5; cursor: not-allowed; }

.fm-component { animation: fmSlideUp 0.35s ease-out; }
@keyframes fmSlideUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }

.fm-lazy-prompt { padding: 0; }
.fm-lazy-card {
  background: #fff;
  border: 1px solid #E5E1DA;
  border-radius: 16px;
  padding: 40px 30px;
  text-align: center;
}
.fm-lazy-icon {
  width: 78px; height: 78px; border-radius: 22px;
  background: #FFF1E4; color: #EE7A22;
  display: inline-flex; align-items: center; justify-content: center;
  margin-bottom: 16px;
  font-size: 34px;
}
.fm-lazy-card h4 {
  font-family: 'Sora', sans-serif;
  font-size: 20px; font-weight: 700;
  color: #221F1C; margin: 0 0 8px;
}
.fm-lazy-card p {
  color: #736C63;
  font-size: 15px;
  max-width: 50ch; margin: 0 auto 18px;
  line-height: 1.55;
}

.fm-loading-inline {
  display: flex; align-items: center; justify-content: center;
  gap: 12px;
  color: #736C63;
  font-weight: 600;
  padding: 40px 20px;
}

/* ===================================================== */
/* MODAL                                                 */
/* ===================================================== */
.fm-modal.modal-content {
  border: 0; border-radius: 20px; overflow: hidden;
  box-shadow: 0 30px 70px -20px rgba(34, 31, 28, 0.5);
}
.fm-modal .modal-header {
  background: #221F1C !important;
  color: #fff !important;
  border: 0 !important;
  border-bottom: 4px solid #EE7A22 !important;
  padding: 16px 22px !important;
}
.fm-modal .modal-header .close { color: #fff !important; opacity: 0.85; text-shadow: none; }
.fm-modal .modal-body { background: #F6F4F0; padding: 20px 22px; }

.fm-modal-title { display: flex; align-items: center; gap: 14px; }
.fm-modal-title-icon {
  width: 42px; height: 42px; border-radius: 12px;
  background: rgba(238, 122, 34, 0.2);
  color: #FFB06B;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 18px;
}
.fm-modal-title-main { font-family: 'Sora', sans-serif; font-size: 19px; font-weight: 700; }
.fm-modal-title-sub { font-size: 13px; color: rgba(255, 255, 255, 0.65); }

.fm-modal-body { display: flex; flex-direction: column; gap: 14px; }

.popup-main-value {
  font-family: 'Sora', sans-serif;
  font-size: 40px; font-weight: 800;
  text-align: center;
  padding: 18px 0 6px;
  color: #221F1C;
}
.popup-main-value.trend-positive { color: #0F6E6B; }
.popup-main-value.trend-negative { color: #C93C30; }

.popup-trend {
  display: inline-flex; align-items: center; justify-content: center;
  gap: 8px;
  font-size: 14.5px; font-weight: 700;
  color: #4A4540;
  background: #F7F5F2;
  border: 1px solid #E5E1DA;
  border-radius: 999px;
  padding: 5px 14px;
  align-self: center;
}

.popup-breakdown h6 {
  font-family: 'Sora', sans-serif;
  font-size: 13.5px;
  font-weight: 700;
  color: #736C63;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin: 12px 0 8px;
}
.popup-breakdown ul {
  list-style: none; margin: 0; padding: 0;
  display: flex; flex-direction: column; gap: 6px;
}
.popup-breakdown li {
  display: flex; justify-content: space-between; align-items: center;
  background: #fff;
  border: 1px solid #E5E1DA;
  border-left: 4px solid #EE7A22;
  border-radius: 10px;
  padding: 11px 14px;
  font-size: 14.5px;
  animation: fmFadeUp 0.3s ease-out backwards;
}
.popup-breakdown li span { color: #736C63; font-weight: 600; }
.popup-breakdown li strong { color: #221F1C; font-family: 'Sora', sans-serif; font-weight: 700; }
@keyframes fmFadeUp { from { opacity: 0; transform: translateX(-12px); } to { opacity: 1; transform: translateX(0); } }

.fm-modal-foot {
  display: flex; justify-content: flex-end; gap: 10px;
  padding-top: 12px;
  border-top: 1px solid #E5E1DA;
}
.fm-btn-ghost.btn {
  background: #fff !important;
  color: #221F1C !important;
  border: 1.5px solid #E5E1DA !important;
  border-radius: 10px !important;
  font-weight: 700; font-size: 14.5px;
  padding: 8px 18px;
}
.fm-btn-ghost.btn:hover { border-color: #EE7A22 !important; color: #D4620F !important; }
.fm-btn-primary.btn {
  background: #EE7A22 !important;
  border: 1.5px solid #EE7A22 !important;
  color: #fff !important;
  border-radius: 10px !important;
  font-weight: 700; font-size: 14.5px;
  padding: 8px 18px;
  display: inline-flex; align-items: center; gap: 8px;
}
.fm-btn-primary.btn:hover { background: #D4620F !important; border-color: #D4620F !important; }

/* ===================================================== */
/* REDUCED MOTION                                        */
/* ===================================================== */
@media (prefers-reduced-motion: reduce) {
  .fm-hero-wave, .fm-logo--lg, .fm-tab-fade-enter-active, .fm-tab-fade-leave-active,
  .fm-component, .popup-breakdown li { animation: none !important; }
}
</style>


// WEBPACK FOOTER //
// src/components/Home.vue